import { FC, useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { z } from 'zod';

import { useTranslationPrefix } from 'infrastructure/translations/i18n';

import { CustomerData } from './CustomerData';

const emailSchema = z.string().email();

export const CloseoutEmailAddresses: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const t = useTranslationPrefix(
    'configuration.customer_details.customer_form.closeout_email_addresses',
  );
  const { values, setFieldValue } = useFormikContext<CustomerData>();
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const emailAddresses = values.closeoutEmailAddresses || [];

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim();

    if (!trimmedEmail) {
      setEmailError(t('email_required'));
      return;
    }

    const result = emailSchema.safeParse(trimmedEmail);
    if (!result.success) {
      setEmailError(t('email_invalid'));
      return;
    }

    if (emailAddresses.includes(trimmedEmail)) {
      setEmailError(t('email_duplicate'));
      return;
    }

    setFieldValue('closeoutEmailAddresses', [...emailAddresses, trimmedEmail]);
    setNewEmail('');
    setEmailError('');
  };

  const handleDeleteEmail = (index: number) => {
    const updatedEmails = emailAddresses.filter((_, i) => i !== index);
    setFieldValue('closeoutEmailAddresses', updatedEmails);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('description')}
      </Typography>

      {/* Input field for adding new email */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={t('add_email_label')}
          placeholder={t('add_email_placeholder')}
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            if (emailError) setEmailError('');
          }}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          error={!!emailError}
          helperText={emailError}
        />
        <IconButton
          color="primary"
          onClick={handleAddEmail}
          disabled={disabled || !newEmail.trim()}
          aria-label={t('add_button_aria')}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* List of email addresses */}
      {emailAddresses.length > 0 ? (
        <List
          dense
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
        >
          {emailAddresses.map((email, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label={t('delete_button_aria')}
                  onClick={() => handleDeleteEmail(index)}
                  disabled={disabled}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={email} />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('no_emails')}
        </Typography>
      )}
    </Box>
  );
};
